import { useEffect, useRef } from 'react';

/**
 * Calls `onMore` whenever the returned sentinel scrolls into (or is still in) view.
 * Re-observes when `watch` changes, so a sentinel left on screen after a short page loads the next one.
 */
export function useInfiniteScroll(onMore: () => void, enabled: boolean, watch: unknown): (el: HTMLElement | null) => void {
  const node = useRef<HTMLElement | null>(null);
  const latest = useRef(onMore);
  latest.current = onMore;

  useEffect(() => {
    const el = node.current;
    if (!enabled || !el) return;
    if (typeof IntersectionObserver === 'undefined') { latest.current(); return; }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) latest.current();
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, watch]);

  return el => { node.current = el; };
}
